// Réplica de la tarjeta pública (Card) para mostrarla dentro de la vista previa
// de aprobación. Usa un carrusel propio (sin Swiper) con las fotos ya cargadas.

import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import styles from './CardPreview.module.css';
import {
  FaWhatsapp,
  FaEnvelope,
  FaRulerCombined,
  FaExpand,
  FaCouch,
  FaBath,
} from 'react-icons/fa';
import title from '../../Assets/titulo.png';

const isTruthy = (value) =>
  value === true || value === 'true' || value === 1 || value === '1';

const CardPreview = (props) => {
  const [contact, setContact] = useState(null);
  const [isMartiller, setIsMartiller] = useState(false);
  const [agentPhotoLoaded, setAgentPhotoLoaded] = useState(false);
  const [current, setCurrent] = useState(0);
  const hoverRef = useRef(false);

  const formatPrice = useCallback((price) => {
    if (price) {
      const cleanedPrice = price.toString().replace(/\./g, '');
      return cleanedPrice.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    }
    return price;
  }, []);

  const fetchContact = useCallback(async () => {
    try {
      let response;
      if (props.martillerId !== null && props.martillerId !== undefined) {
        response = await axios.get(`/martiller/${props.martillerId}`);
        setIsMartiller(true);
      } else if (props.sellerId !== null && props.sellerId !== undefined) {
        response = await axios.get(`/seller/${props.sellerId}`);
        setIsMartiller(false);
      }
      if (response) setContact(response.data.data);
    } catch (error) {
      console.error('Error al obtener los datos de contacto:', error);
    }
  }, [props.martillerId, props.sellerId]);

  useEffect(() => {
    fetchContact();
  }, [fetchContact]);

  const photos = props.photo || [];

  // Carrusel automático simple.
  useEffect(() => {
    if (photos.length <= 1) return undefined;
    const id = setInterval(() => {
      if (!hoverRef.current) {
        setCurrent((c) => (c + 1) % photos.length);
      }
    }, 3000);
    return () => clearInterval(id);
  }, [photos.length]);

  useEffect(() => {
    if (current > photos.length - 1) setCurrent(0);
  }, [photos.length, current]);

  const services = props.services || {};
  const mainServices = [
    { key: 'agua', label: 'Agua' },
    { key: 'electricidad', label: 'Luz' },
    { key: 'gas', label: 'Gas' },
  ].filter((service) => isTruthy(services[service.key]));

  const expensesNumber = props.expenses
    ? parseFloat(String(props.expenses).replace(/[^\d]/g, '')) || 0
    : 0;
  const hasExpenses = expensesNumber > 0;

  return (
    <div className={styles.card}>
      <div
        className={styles.imageContainer}
        onMouseEnter={() => {
          hoverRef.current = true;
        }}
        onMouseLeave={() => {
          hoverRef.current = false;
        }}
      >
        {photos.length > 0 ? (
          <>
            {photos.map((image, index) => (
              <img
                key={index}
                src={image}
                className={`${styles['carousel-img']} ${
                  index === current ? styles.imgActive : styles.imgHidden
                }`}
                alt={`Slide ${index}`}
                loading={index === 0 ? 'eager' : 'lazy'}
              />
            ))}
            {photos.length > 1 && (
              <div className={styles.dots}>
                {photos.map((_, index) => (
                  <button
                    key={index}
                    type="button"
                    className={`${styles.dot} ${
                      index === current ? styles.dotActive : ''
                    }`}
                    onClick={() => setCurrent(index)}
                    aria-label={`Ir a la foto ${index + 1}`}
                  />
                ))}
              </div>
            )}
          </>
        ) : (
          <p>No hay imágenes disponibles.</p>
        )}
      </div>
      <div className={styles.detailsCardContainer}>
        <div className={styles.link}>
          <p className={styles.price}>
            {formatPrice(props.price)} {props.currency}
          </p>
          {hasExpenses && (
            <p className={styles.expenses}>
              + {formatPrice(props.expenses)} {props.currencyExpenses} de
              expensas
            </p>
          )}
          <p className={styles.street}>
            {props.street} {props.number}
          </p>
          <p className={styles.locality}>
            {props.locality}, {props.departments}, {props.province}
          </p>
          <div className={styles.datos}>
            {props.totalSquareMeters > 0 && (
              <div>
                <FaRulerCombined className={styles.datoIcon} />
                <p>{props.totalSquareMeters} m² Totales</p>
              </div>
            )}
            {props.coveredSquareMeters > 0 && (
              <div>
                <FaExpand className={styles.datoIcon} />
                <p>{props.coveredSquareMeters} m² Cubiertos</p>
              </div>
            )}
            {props.environments > 0 && (
              <div>
                <FaCouch className={styles.datoIcon} />
                <p>{props.environments} Ambientes</p>
              </div>
            )}
            {props.bathrooms > 0 && (
              <div>
                <FaBath className={styles.datoIcon} />
                <p>{props.bathrooms} Baños</p>
              </div>
            )}
          </div>
          {mainServices.length > 0 && (
            <div className={styles.servicios}>
              {mainServices.map((service) => (
                <span key={service.key} className={styles.servicioChip}>
                  {service.label}
                </span>
              ))}
            </div>
          )}
          <div className={styles.tituloContainer}>
            <p className={styles.titulo}>{props.title}</p>
          </div>
        </div>
        {contact && (
          <div className={styles.sellerContainer}>
            <img
              src={agentPhotoLoaded && contact.photo ? contact.photo : title}
              alt="Agente"
              className={styles.sellerimg}
              onLoad={() => setAgentPhotoLoaded(true)}
              onError={() => setAgentPhotoLoaded(false)}
              loading="lazy"
            />
            <div className={styles.sellerDetails}>
              <p className={styles.vendedorName}>
                {contact.name} {contact.last_name}
              </p>
              {isMartiller && contact.matricula && (
                <p className={styles.vendedorName}>{contact.matricula}</p>
              )}
              <div className={styles.comunication}>
                <a
                  href={`mailto:${contact.mail}`}
                  className={styles.emailButton}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Email"
                >
                  <FaEnvelope className={styles.contactIcon} />
                </a>
                <a
                  href={`https://wa.me/${contact.phone_number}`}
                  className={styles.whatsappButton}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="WhatsApp"
                >
                  <FaWhatsapp className={styles.contactIcon} />
                </a>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CardPreview;
